import { useBehavior, useButton, useDetectDocumentScroll, useTriggerAction } from '@libs/core';
import { TAssetsSvgMapValue, TButtonData, TWidgetProps } from '@libs/types';
import React from 'react';
import { TButtonOption } from '../../types/options';
import { ButtonCircleView } from './ButtonCircleView';

const SIZE_CONFIG: Record<string, { circleSize: number; iconSize: number }> = {
    small: { circleSize: 28, iconSize: 16 },
    medium: { circleSize: 40, iconSize: 24 },
    large: { circleSize: 56, iconSize: 24 },
};

/**
 * Standalone ButtonCircle widget driven entirely by server-provided configuration.
 * Owns behavior hooks (useBehavior, useButton, useDetectDocumentScroll) and
 * delegates all rendering to ButtonCircleView.
 *
 * Use this component when the widget definition comes from the server and no
 * additional style overrides are required.
 */
export const ButtonCircleWidget = ({ widget }: TWidgetProps<TButtonData, TButtonOption>) => {
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });

    const { documentScrolled } = useDetectDocumentScroll(widget.uid);

    const options = widget.options ?? {};
    const iconName = options.iconName as TAssetsSvgMapValue | undefined;
    const hierarchy = options.hierarchy ?? 'tertiary';
    const size = options.size ?? 'medium';
    const { iconSize } = SIZE_CONFIG[size] ?? SIZE_CONFIG.large;

    const dynamicShadow = options.dynamicShadow ?? true;
    const effectivePosition = dynamicShadow && documentScrolled && !isDisabled ? 'floating' : options.position;
    const raised = effectivePosition === 'floating' && !isDisabled;

    const parentStyleNames = [options.style].filter(Boolean) as string[];

    return (
        <ButtonCircleView
            widget={widget}
            iconName={iconName}
            label={widget.data?.messages?.label ?? widget.data?.label}
            size={size}
            hierarchy={hierarchy}
            margin={options.margin}
            isDisabled={isDisabled}
            isHide={isHidden}
            raised={raised}
            iconWidth={iconSize}
            iconHeight={iconSize}
            parentStyleNames={parentStyleNames}
            onClick={() => handleClick()}
        />
    );
};
